import React, { createContext, PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { v4 } from 'uuid'
import { GoogleAnalytics } from '../analytics/GoogleAnalytics'
import { useLocales } from '../state'
import { LocalStorage } from '../storage'
import { logger } from '../logger'
import { getStatus } from '../handlers/walletActions/wallet'
import { deviceInfo } from '../utils/deviceInfo'
import { GAEvent, GAEventName } from '../interfaces/googleAnalytics'

export const GoogleAnalyticsContext = createContext<{
  fireEvent: (name: GAEventName, eventData: GAEvent) => Promise<void>
}>({
  fireEvent: async () => {},
})

export const GoogleAnalyticsProvider = ({ children }: PropsWithChildren) => {
  const { userLocale } = useLocales()
  const ga = useRef<GoogleAnalytics>()

  useEffect(() => {
    const getGA = async () => {
      let clientId = await LocalStorage.get('clientId')
      if (!clientId) {
        clientId = v4()
        LocalStorage.set('clientId', clientId)
      }

      ga.current = new GoogleAnalytics(clientId, v4())
    }

    getGA()
  }, [])

  const fireEvent = async (name: GAEventName, eventData: GAEvent) => {
    try {
      const status = await getStatus()

      const userContext = {
        registered: status.hasVault && status.hasPassword,
        region: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: userLocale.id,
        ...deviceInfo,
      }

      await ga.current?.fireEvent(name, eventData, userContext)
    } catch (e) {
      logger.debug('Failed to send GA event', { originalError: e })
    }
  }

  return (
    <GoogleAnalyticsContext.Provider value={{ fireEvent }}>
      {children}
    </GoogleAnalyticsContext.Provider>
  )
}

export const useGoogleAnalytics = () => useContext(GoogleAnalyticsContext)
